import {
  FixedFeeConfig,
  Adjustment,
  ReclassBalance,
} from "@amzn/fuse-accounting-central-datastore-client";
import { ReportRow } from "./types";
import { toNum, getFirstOfMonth } from "./utils";

export function computeReportRow(
  config: FixedFeeConfig,
  reportDate: string,
  reclassCutoff: string,
): ReportRow | null {
  const env = config.environment;
  const status = config.dealStatus;
  if (env !== "PRODUCTION" || status !== "ENABLED") return null;

  const dealStart = config.dealStartDate ?? "";
  const dealEnd = config.dealEndDate ?? "";
  const reportMonthStart = getFirstOfMonth(reportDate);

  // Deal must be active during report month
  if (dealEnd && dealEnd < reportMonthStart) return null;
  if (dealStart && dealStart > reportDate) return null;

  const contractPrice = toNum(config.contractPrice);
  const rb: ReclassBalance | undefined = config.reclassBalance;
  const hasReclass =
    rb != null &&
    (rb.unbilledAccountsReceivables != null || rb.longTermReceivables != null);

  const ltIssuance = hasReclass
    ? rb!.longTermReceivables != null
      ? toNum(rb!.longTermReceivables)
      : 0
    : 0;
  const stIssuance = hasReclass
    ? rb!.unbilledAccountsReceivables != null
      ? toNum(rb!.unbilledAccountsReceivables)
      : contractPrice - ltIssuance
    : contractPrice;

  // Invoices issued: invoice date (map key) <= report date
  const invoiceBreakdown = config.invoiceBreakdown ?? {};
  let invoicesIssued = 0;
  for (const [invoiceDate, detail] of Object.entries(invoiceBreakdown)) {
    if (invoiceDate <= reportDate) {
      invoicesIssued += toNum(detail.invoiceAmount);
    }
  }

  // Cumulative reclassification: sum receivableShiftAmount where adj date <= reclassCutoff
  const adjustments: Record<string, Adjustment> = config.adjustments ?? {};
  let cumulativeReclass = 0;
  for (const [adjDate, adj] of Object.entries(adjustments)) {
    if (adjDate <= reclassCutoff && adj.receivableShiftAmount != null) {
      cumulativeReclass += toNum(adj.receivableShiftAmount);
    }
  }

  const resellerId = config.dealConfig?.resellerIds?.[0] ?? "";

  // Automation Status: check if most recent executed adjustment (by date <= reclassCutoff) has non-zero amount
  let automationStatus = "Manual";
  const executedByDate = Object.entries(adjustments)
    .filter(
      ([date, adj]) => date <= reclassCutoff && adj.adjustmentAmount != null,
    )
    .sort(([a], [b]) => b.localeCompare(a));
  if (executedByDate.length > 0) {
    const [, mostRecent] = executedByDate[0];
    automationStatus =
      toNum(mostRecent.adjustmentAmount) !== 0 ? "Automatic" : "Manual";
  }

  return {
    fixedFeeId: config.fixedFeeId ?? "",
    companyCode: "",
    resellerId,
    region: "",
    dealStartDate: dealStart,
    dealEndDate: dealEnd,
    automationStatus,
    contractPrice,
    currencyCode: config.contractCurrencyCode ?? "",
    fxRate: 1.0, // TODO: populate from ARD functionalCurrency/FX rate
    funcCurrency: config.contractCurrencyCode ?? "", // Same as contract until ARD join
    stIssuance,
    ltIssuance,
    cumulativeReclass,
    invoicesIssued,
    stEnding: stIssuance + cumulativeReclass - invoicesIssued,
    ltEnding: ltIssuance - cumulativeReclass,
  };
}
